import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

// ─── Self-healing chunk-error recovery ────────────────────────────────────
// When the service worker has a stale shell cache (old webpack chunk hash
// pointing to a file the dev/prod server no longer serves), the browser
// fetches an HTML SPA fallback for what it thinks is JS and throws either:
//   • SyntaxError: Unexpected token '<'
//   • ChunkLoadError: Loading chunk N failed
// Either way the cleanest recovery is to ask the SW to purge every
// `tsogn-*` cache and reload. We rate-limit to one purge per session to
// avoid an infinite reload loop if the network itself is broken.
(function installChunkErrorRecovery() {
  if (typeof window === 'undefined') return;
  const FLAG = '__tsogn_chunk_purge_attempted__';
  const isChunkErr = (msg) =>
    typeof msg === 'string' && (
      msg.includes("Unexpected token '<'") ||
      msg.includes('ChunkLoadError') ||
      msg.includes('Loading chunk') ||
      msg.includes('Failed to fetch dynamically imported module')
    );
  const heal = (label) => {
    if (sessionStorage.getItem(FLAG)) return;       // only once per session
    sessionStorage.setItem(FLAG, '1');
    // eslint-disable-next-line no-console
    console.warn(`[TSOGN] ${label} — purging service-worker caches and reloading`);
    if (navigator.serviceWorker?.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'PURGE' });
      const onPurged = (event) => {
        if (event.data?.type === 'PURGED') {
          navigator.serviceWorker.removeEventListener('message', onPurged);
          window.location.reload();
        }
      };
      navigator.serviceWorker.addEventListener('message', onPurged);
      // Hard fallback if the SW never responds within 1.5s.
      setTimeout(() => window.location.reload(), 1500);
    } else {
      // No SW yet — wipe caches directly then reload.
      if ('caches' in window) {
        caches.keys().then((keys) => Promise.all(
          keys.filter((k) => k.startsWith('tsogn-')).map((k) => caches.delete(k))
        )).finally(() => window.location.reload());
      } else {
        window.location.reload();
      }
    }
  };
  window.addEventListener('error', (e) => {
    if (isChunkErr(e.message)) heal('chunk syntax error');
  });
  window.addEventListener('unhandledrejection', (e) => {
    const msg = e.reason?.message || String(e.reason || '');
    if (isChunkErr(msg)) heal('chunk promise rejection');
  });
})();

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
